/* =============================================================
   VisaQuizContext — Global context for opening VisaQuiz
   from anywhere in the app (Header, Hero, etc.)
   When opened without a country, shows country selection first.
   ============================================================= */
import { createContext, useContext, useState, type ReactNode } from "react";
import VisaQuiz from "@/components/VisaQuiz";

interface QuizCountry {
  id: string;
  flag: string;
  name: string;
}

export type OpenVisaQuiz = (country?: QuizCountry) => void;

const VisaQuizContext = createContext<OpenVisaQuiz>(() => {});

export function useVisaQuiz() {
  return useContext(VisaQuizContext);
}

export function VisaQuizProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [country, setCountry] = useState<QuizCountry | undefined>(undefined);

  const openQuiz: OpenVisaQuiz = (c) => {
    setCountry(c);
    setIsOpen(true);
  };

  return (
    <VisaQuizContext.Provider value={openQuiz}>
      {children}
      {isOpen && (
        <VisaQuiz
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          countryId={country?.id ?? ""}
          countryFlag={country?.flag ?? ""}
          countryName={country?.name ?? ""}
        />
      )}
    </VisaQuizContext.Provider>
  );
}
